import {
  Divider,
  Box,
  Button,
  Card,
  CardHeader,
  Container,
  Grid,
  Skeleton,
  Typography,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import { Outlet, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TabPanel from '../../../../components/molecules/tab-panel';
import { HostsTableSimple } from '../../../../components/organisms/common/hosts-table-simple';
import { ArrowLeft as ArrowLeftIcon } from '../../../../icons/arrow-left';
import { ExclamationOutlined as ExclamationOutlinedIcon } from '../../../../icons/exclamation-outlined';
import { useDestinationGroup } from '../../hooks/query/use-destination-group';
import { DestinationsTable } from '../organisms/destinations-table';
import RemoveDestinationGroupDialog from '../organisms/dialogs/remove-destination-group-dialog';
import { UpdateDestinationGroupDialog } from '../organisms/dialogs/update-destination-group-dialog/update-destination-group-dialog';

import { Pagination } from '@/components/organisms/pagination';
import { useHostsWithSearch } from '@/features/hosts/hooks/query/use-hosts-with-search';
import { useDialog } from '@/hooks/use-dialog';
import { useNavigateBack } from '@/hooks/use-navigate-back';
import { usePagination } from '@/hooks/use-pagination';
import { Can, withPermissions } from '@/permissions/can';

const DestinationGroup = () => {
  const { t } = useTranslation(['destinations', 'hosts', 'destinationGroups', 'host', 'common']);

  const destinationGroupId = useParams({
    from: '/_layout/destination-groups/$destinationGroupId',
    select: state => state.destinationGroupId,
  });

  const { navigateBack } = useNavigateBack();

  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    {
      label: t('destinations:title'),
    },
    {
      label: t('hosts:title'),
    },
  ];

  const {
    data: destinationGroupData,
    isLoading: isLoadingDestinationGroup,
    error: destinationGroupError,
  } = useDestinationGroup(destinationGroupId ? +destinationGroupId : 0);

  const { page, perPage, changePage, changePerPage, setTotalItems, totalPages } = usePagination();

  const {
    data: hostsData,
    isLoading: isLoadingHosts,
    isFetching: isFetchingHosts,
    error: hostsError,
  } = useHostsWithSearch({ page, perPage, destinationGroupId });

  const [isOpenUpdateGroupDialog, handleOpenUpdateGroupDialog, handleCloseUpdateGroupDialog] =
    useDialog();

  const [isOpenDeleteGroupDialog, handleOpenDeleteGroupDialog, handleCloseDeleteGroupDialog] =
    useDialog();

  useEffect(() => {
    if (hostsData) {
      setTotalItems(hostsData.total);
    }
  }, [hostsData]);

  const renderContent = () => {
    if (isLoadingDestinationGroup) {
      return (
        <Box sx={{ py: 4 }}>
          <Skeleton height={42} />
          <Skeleton />
          <Skeleton />
        </Box>
      );
    }

    if (destinationGroupError) {
      return (
        <Box sx={{ py: 4 }}>
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
              p: 3,
            }}>
            <ExclamationOutlinedIcon />
            <Typography color="textSecondary" sx={{ mt: 2 }} variant="body2">
              {destinationGroupError?.response?.data?.message}
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ py: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Button
              color="primary"
              startIcon={<ArrowLeftIcon />}
              onClick={() => navigateBack('/destination-groups')}
              variant="text">
              {t('host:back')}
            </Button>
          </Box>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
            }}>
            <Typography color="textPrimary" variant="h4">
              {destinationGroupData?.name}
            </Typography>
            <Box>
              <Can I="manageGroupDestinations" a="DestinationsGroups" passThrough>
                {allowed => (
                  <Tooltip
                    title={t('common:notSufficientPermissions')}
                    disableHoverListener={allowed}
                    disableFocusListener={allowed}>
                    <span>
                      <Button
                        sx={{ mr: 2 }}
                        onClick={handleOpenUpdateGroupDialog}
                        size="large"
                        variant="outlined"
                        disabled={!allowed}>
                        {t('destinationGroups:edit')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Can>
              <Can I="delete" a="DestinationsGroups" passThrough>
                {allowed => (
                  <Tooltip
                    title={t('common:notSufficientPermissions')}
                    disableHoverListener={allowed}
                    disableFocusListener={allowed}>
                    <span>
                      <Button
                        onClick={handleOpenDeleteGroupDialog}
                        size="large"
                        color="error"
                        variant="outlined"
                        disabled={!allowed}>
                        {t('destinationGroups:delete')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Can>
            </Box>
          </Box>
          <Tabs
            allowScrollButtonsMobile
            value={activeTab}
            variant="scrollable"
            onChange={(e, newValue) => setActiveTab(newValue)}>
            {tabs.map(option => (
              <Tab key={option.label} label={option.label} />
            ))}
          </Tabs>
          <Divider sx={{ mb: 2 }} />
          <TabPanel value={activeTab} index={0}>
            <Grid
              container
              item
              lg={12}
              spacing={3}
              sx={{
                height: 'fit-content',
                order: {
                  md: 2,
                  xs: 1,
                },
              }}
              xs={12}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title={t('destinationGroups:tableDestinations.cardTitle')} />
                  <Divider />
                  <DestinationsTable
                    destinations={destinationGroupData?.destinations || []}
                    isLoading={isLoadingDestinationGroup}
                  />
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <Grid
              container
              item
              lg={12}
              spacing={3}
              sx={{
                height: 'fit-content',
                order: {
                  md: 2,
                  xs: 1,
                },
              }}
              xs={12}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardHeader title={t('destinationGroups:host.cardTitle')} />
                  <Divider />
                  <HostsTableSimple
                    hosts={hostsData?.resources || []}
                    isLoading={isLoadingHosts}
                    error={hostsError?.response?.data?.message || ''}
                  />
                  <Divider sx={{ mt: 'auto' }} />
                  <Pagination
                    changePage={changePage}
                    page={page}
                    perPage={perPage}
                    changePerPage={changePerPage}
                    isFetching={isFetchingHosts}
                    totalPages={totalPages}
                  />
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Box>
        <Outlet />
      </>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        flexGrow: 1,
      }}>
      <Container
        maxWidth="lg"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}>
        {renderContent()}
      </Container>
      <UpdateDestinationGroupDialog
        open={isOpenUpdateGroupDialog}
        onCancel={handleCloseUpdateGroupDialog}
        id={destinationGroupData?.id || 0}
        name={destinationGroupData?.name || ''}
        destinationsIds={destinationGroupData?.destinations?.map(({ id }) => id) || []}
      />
      <RemoveDestinationGroupDialog
        open={isOpenDeleteGroupDialog}
        groupId={destinationGroupData?.id || 0}
        groupName={destinationGroupData?.name || ''}
        onCancel={handleCloseDeleteGroupDialog}
      />
    </Box>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'DestinationsGroups'),
  DestinationGroup,
);
